import { doc, updateDoc } from "firebase/firestore";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { PostCard, StateBlock } from "../components/Cards";
import { displayUserName, UserAvatar } from "../components/UserAvatar";
import { useAuth } from "../lib/auth";
import {
  conversationPathForUsers,
  getAllSearchPosts,
  getConversation,
  getConversations,
  getDirectMessages,
  getPost,
  getUser,
  getUsersByIds,
  POSTS_COLLECTION,
  sendDirectMessage,
  toggleFollow,
  TOP_POSTS_COLLECTION
} from "../lib/data";
import { db } from "../lib/firebase";
import { setSeo } from "../lib/seo";
import { useAsync } from "../lib/useAsync";
import type { ForumUser, Post } from "../lib/types";

export function SearchPage() {
  const [term, setTerm] = useState("");
  const { data, loading, error } = useAsync(getAllSearchPosts, []);
  const results = useMemo(() => {
    const query = term.trim().toLowerCase();
    if (!query) return [];
    return (data || []).filter(({ post }) => {
      return post.title.toLowerCase().includes(query) || post.content.toLowerCase().includes(query);
    });
  }, [data, term]);

  useEffect(() => setSeo({ title: "Search | My Local Forum", path: "/search" }), []);

  return (
    <section className="page">
      <h1>Search</h1>
      <input className="search-input" value={term} onChange={(event) => setTerm(event.target.value)} placeholder="Search discussions" />
      <StateBlock loading={loading && term.trim().length > 0} error={error} empty={term.trim().length > 0 && results.length === 0 && !loading} />
      <div className="masonry">
        {results.map(({ post, collectionName }) => (
          <PostCard key={`${collectionName}:${post.id}`} post={post} collectionName={collectionName} />
        ))}
      </div>
    </section>
  );
}

export function BookmarksPage() {
  const { user } = useAuth();
  const { data: profile } = useAsync(() => (user ? getUser(user.uid) : Promise.resolve(null)), [user?.uid]);
  const { data, loading, error } = useAsync(() => loadBookmarks(profile), [profile?.collectedPosts?.join("|")]);
  useEffect(() => setSeo({ title: "Bookmarks | My Local Forum", path: "/bookmarks", noindex: true }), []);

  if (!user) {
    return (
      <section className="page">
        <h1>Bookmarks</h1>
        <Link className="primary-button" to="/auth">Sign in to view bookmarks</Link>
      </section>
    );
  }

  return (
    <section className="page">
      <h1>Bookmarks</h1>
      <StateBlock loading={loading} error={error} empty={data?.length === 0} />
      <div className="masonry">
        {data?.map(({ post, collectionName }) => (
          <PostCard key={`${collectionName}:${post.id}`} post={post} collectionName={collectionName} />
        ))}
      </div>
    </section>
  );
}

async function loadBookmarks(profile: ForumUser | null): Promise<Array<{ post: Post; collectionName: string }>> {
  if (!profile?.collectedPosts?.length) return [];
  const loaded = await Promise.all(
    profile.collectedPosts.map(async (key) => {
      const [collectionName, postId] = key.includes(":") ? key.split(":", 2) : [POSTS_COLLECTION, key];
      const post = await getPost(collectionName, postId);
      if (post) return { post, collectionName };
      if (!key.includes(":")) {
        const topPost = await getPost(TOP_POSTS_COLLECTION, key);
        if (topPost) return { post: topPost, collectionName: TOP_POSTS_COLLECTION };
      }
      return null;
    })
  );
  return loaded.filter(Boolean) as Array<{ post: Post; collectionName: string }>;
}

export function InboxPage() {
  const { user } = useAuth();
  const { data, loading, error } = useAsync(() => (user ? getConversations(user.uid) : Promise.resolve([])), [user?.uid]);
  const otherUserIds = useMemo(
    () => (user ? (data || []).map((conversation) => conversation.participants.find((id) => id !== user.uid) || "").filter(Boolean) : []),
    [data, user?.uid]
  );
  const { data: usersById } = useAsync(() => getUsersByIds(otherUserIds), [otherUserIds.join("|")]);
  useEffect(() => setSeo({ title: "Inbox | My Local Forum", path: "/inbox", noindex: true }), []);

  if (!user) {
    return (
      <section className="page">
        <h1>Inbox</h1>
        <Link className="primary-button" to="/auth">Sign in to view inbox</Link>
      </section>
    );
  }

  return (
    <section className="page">
      <h1>Inbox</h1>
      <StateBlock loading={loading} error={error} empty={data?.length === 0} />
      <div className="list">
        {data?.map((conversation) => {
          const otherUserId = conversation.participants.find((id) => id !== user.uid) || "";
          const otherUser = usersById?.[otherUserId];
          return (
          <Link className="panel link-card conversation-link" to={`/inbox/${encodeURIComponent(conversation.id)}`} key={conversation.id}>
            <UserAvatar name={displayUserName(otherUser, "User")} imageUrl={otherUser?.profilePictureUrl} />
            <span className="conversation-copy">
              <strong>{displayUserName(otherUser, "User")}</strong>
              <span>{conversation.lastMessage || "No messages yet"}</span>
            </span>
          </Link>
        );
        })}
      </div>
    </section>
  );
}

export function DirectMessagePage() {
  const { conversationId = "" } = useParams();
  const { user } = useAuth();
  const { data: conversation, loading, error } = useAsync(
    () => (conversationId ? getConversation(conversationId) : Promise.resolve(null)),
    [conversationId]
  );
  const otherUserId = user && conversation ? conversation.participants.find((id) => id !== user.uid) || "" : "";
  const { data: profile } = useAsync(() => (user ? getUser(user.uid) : Promise.resolve(null)), [user?.uid]);
  const { data: otherUser } = useAsync(() => (otherUserId ? getUser(otherUserId) : Promise.resolve(null)), [otherUserId]);
  const { data: messages } = useAsync(
    () => (conversation ? getDirectMessages(conversation.id) : Promise.resolve([])),
    [conversation?.id]
  );
  const canMessage =
    Boolean(user && otherUserId) &&
    profile?.following?.includes(otherUserId) === true &&
    otherUser?.following?.includes(user?.uid || "") === true;

  useEffect(() => setSeo({ title: "Messages | My Local Forum", path: `/inbox/${conversationId}`, noindex: true }), [conversationId]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || !otherUserId || !canMessage) return;
    const form = new FormData(event.currentTarget);
    const text = String(form.get("text") || "").trim();
    if (!text) return;
    await sendDirectMessage({
      conversationId,
      senderId: user.uid,
      receiverId: otherUserId,
      text
    });
    event.currentTarget.reset();
    window.location.reload();
  }

  if (!user) {
    return (
      <section className="page">
        <h1>Messages</h1>
        <Link className="primary-button" to="/auth">Sign in to view messages</Link>
      </section>
    );
  }

  return (
    <section className="page detail">
      <Link className="text-link" to="/inbox">
        Back to inbox
      </Link>
      <div className="profile-heading">
        {otherUserId ? (
          <Link to={`/users/${encodeURIComponent(otherUserId)}`} aria-label="View user profile">
            <UserAvatar name={displayUserName(otherUser, "Messages")} imageUrl={otherUser?.profilePictureUrl} size="lg" />
          </Link>
        ) : null}
        <h1>{displayUserName(otherUser, "Messages")}</h1>
      </div>
      <StateBlock loading={loading} error={error} empty={!loading && !conversation} />
      {conversation ? (
        <>
          <div className="message-list">
            {messages?.map((message) => (
              <article className={`comment direct-message ${message.senderId === user.uid ? "is-mine" : ""}`} key={message.id}>
                <div className="message-author">
                  <UserAvatar
                    name={message.senderId === user.uid ? displayUserName(profile, user.email || "You") : displayUserName(otherUser)}
                    imageUrl={message.senderId === user.uid ? profile?.profilePictureUrl || user.photoURL : otherUser?.profilePictureUrl}
                    size="sm"
                  />
                  <strong>{message.senderId === user.uid ? "You" : displayUserName(otherUser)}</strong>
                </div>
                <p>{message.text}</p>
              </article>
            ))}
          </div>
          {!canMessage ? <div className="state">Mutual follow required to send messages.</div> : null}
          <form className="form-grid" onSubmit={submit}>
            <input name="text" placeholder={canMessage ? "Message" : "Mutual follow required"} disabled={!canMessage} />
            <button className="primary-button" type="submit" disabled={!canMessage}>
              Send
            </button>
          </form>
        </>
      ) : null}
    </section>
  );
}

export function ProfilePage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);
  const [followError, setFollowError] = useState<string | null>(null);
  const viewedUserId = userId || user?.uid || "";
  const isOwnProfile = !userId || userId === user?.uid;
  const { data: profile, loading, error } = useAsync(
    () => (viewedUserId ? getUser(viewedUserId) : Promise.resolve(null)),
    [viewedUserId, refreshKey]
  );
  const { data: viewerProfile } = useAsync(
    () => (user ? getUser(user.uid) : Promise.resolve(null)),
    [user?.uid, refreshKey]
  );
  useEffect(() => setSeo({ title: "Profile | My Local Forum", path: userId ? `/users/${userId}` : "/profile", noindex: true }), [userId]);

  const isBlocked = profile ? viewerProfile?.blockedUsers?.includes(profile.uid) === true : false;
  const isFollowing = profile ? viewerProfile?.following?.includes(profile.uid) === true : false;
  const canMessage = Boolean(user && profile) && isFollowing && profile?.following?.includes(user?.uid || "") === true;

  async function onToggleFollow() {
    if (!viewerProfile || !profile || followBusy) return;
    setFollowBusy(true);
    setFollowError(null);
    try {
      await toggleFollow(viewerProfile, profile.uid);
      setRefreshKey((key) => key + 1);
    } catch (err: unknown) {
      setFollowError(err instanceof Error ? err.message : "Failed to update follow status.");
    } finally {
      setFollowBusy(false);
    }
  }

  if (!viewedUserId) {
    return (
      <section className="page">
        <h1>Profile</h1>
        <Link className="primary-button" to="/auth">Sign in</Link>
      </section>
    );
  }

  return (
    <section className="page">
      <h1>Profile</h1>
      <div className="panel profile-panel">
        <StateBlock loading={loading} error={error} empty={!loading && !profile} />
        <UserAvatar name={displayUserName(profile, user?.email || "User")} imageUrl={profile?.profilePictureUrl} size="lg" />
        <div className="profile-summary">
          <strong>{displayUserName(profile, user?.email || "User")}</strong>
          <span>Coins: {profile?.coins || 0}</span>
        </div>
        {profile ? (
          <div className="follow-stats">
            <Link to={`/users/${encodeURIComponent(profile.uid)}/following`}>
              <strong>{profile.following?.length || 0}</strong>
              <span>Following</span>
            </Link>
            <Link to={`/users/${encodeURIComponent(profile.uid)}/followers`}>
              <strong>{profile.followers?.length || 0}</strong>
              <span>Followers</span>
            </Link>
          </div>
        ) : null}
        {isOwnProfile ? <ProfileForm profile={profile} /> : null}
        {isOwnProfile ? (
          <div className="button-list">
            <Link to="/store">Forum Store</Link>
            <Link to="/bookmarks">Bookmarks</Link>
            <Link to="/inbox">Inbox</Link>
          </div>
        ) : user && profile && !isBlocked ? (
          <div className="profile-actions">
            <button className="primary-button full-width" type="button" disabled={!viewerProfile || followBusy} onClick={onToggleFollow}>
              {followBusy ? "Updating..." : isFollowing ? "Following" : "Follow"}
            </button>
            {canMessage ? (
              <Link className="primary-button full-width" to={conversationPathForUsers(user.uid, profile.uid)}>
                Message
              </Link>
            ) : null}
            {!canMessage ? <div className="state compact-state">Mutual follow required to send messages.</div> : null}
            {followError ? <div className="auth-error inline-error">{followError}</div> : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function FollowListPage({ listType }: { listType: "followers" | "following" }) {
  const { userId = "" } = useParams();
  const title = listType === "following" ? "Following" : "Followers";
  const { data: profile, loading, error } = useAsync(() => (userId ? getUser(userId) : Promise.resolve(null)), [userId]);
  const userIds = profile?.[listType] || [];
  const { data: usersById, loading: usersLoading, error: usersError } = useAsync(
    () => getUsersByIds(userIds),
    [userIds.join("|")]
  );
  useEffect(
    () => setSeo({ title: `${title} | My Local Forum`, path: `/users/${userId}/${listType}`, noindex: true }),
    [listType, title, userId]
  );

  const users = userIds.map((id) => usersById?.[id]).filter(Boolean) as ForumUser[];
  const emptyText = listType === "following" ? "Not following anyone yet." : "No followers yet.";

  return (
    <section className="page detail">
      <Link className="text-link" to={userId ? `/users/${encodeURIComponent(userId)}` : "/profile"}>
        Back to profile
      </Link>
      <h1>{title}</h1>
      <StateBlock loading={loading || usersLoading} error={error || usersError} />
      {!loading && !usersLoading && users.length === 0 ? <div className="state compact-state">{emptyText}</div> : null}
      <div className="list">
        {users.map((listedUser) => (
          <Link className="panel link-card conversation-link" to={`/users/${encodeURIComponent(listedUser.uid)}`} key={listedUser.uid}>
            <UserAvatar name={displayUserName(listedUser)} imageUrl={listedUser.profilePictureUrl} />
            <span className="conversation-copy">
              <strong>{displayUserName(listedUser)}</strong>
              <span>{listedUser.email || listedUser.uid}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ProfileForm({ profile }: { profile: ForumUser | null }) {
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!profile) return;
    const form = new FormData(event.currentTarget);
    await updateDoc(doc(db, "users", profile.uid), {
      nickname: String(form.get("nickname") || "").trim(),
      profilePictureUrl: String(form.get("profilePictureUrl") || "").trim()
    });
    window.location.reload();
  }

  return (
    <form className="form-grid" onSubmit={submit}>
      <input name="nickname" placeholder="Nickname" defaultValue={profile?.nickname || ""} />
      <input name="profilePictureUrl" placeholder="Profile picture URL" defaultValue={profile?.profilePictureUrl || ""} />
      <button className="primary-button" type="submit">
        Save Profile
      </button>
    </form>
  );
}

export function SettingsPage() {
  const { user, login, logout } = useAuth();
  const { data: profile } = useAsync(() => (user ? getUser(user.uid) : Promise.resolve(null)), [user?.uid]);
  const role = profile?.role?.toUpperCase();
  const canOpenAdmin = role === "ADMIN" || role === "COADMIN";
  useEffect(() => setSeo({ title: "Settings | My Local Forum", path: "/settings", noindex: true }), []);
  return (
    <section className="page">
      <h1>Settings</h1>
      <div className="button-list">
        {canOpenAdmin ? <Link to="/admin">Admin Dashboard</Link> : null}
        <Link to="/privacy-policy">Privacy Policy</Link>
        {user ? <button onClick={logout}>Logout</button> : <button onClick={login}>Sign in</button>}
      </div>
    </section>
  );
}

export function PrivacyPolicyPage() {
  useEffect(() => setSeo({ title: "Privacy Policy | My Local Forum", path: "/privacy-policy" }), []);
  return (
    <section className="page detail">
      <h1>Privacy Policy</h1>
      <p>
        My Local Forum stores the account and forum data needed to run community features, including posts,
        comments, profile details, messages, reports, and moderation data. Private account pages and direct
        messages are not intended for search indexing.
      </p>
    </section>
  );
}
